import React, { useState, useEffect } from 'react';
import './GameTutorial.css';

function GameTutorial() {
  return (
    <div className="game-tutorials">
      <TutorialFallingLetters />
      <TutorialSelectLetters />
      <TutorialComboSystem />
      <TutorialSpecialTiles />
      <TutorialSkillshot />
      <TutorialPowerUps />
    </div>
  );
}

// Tutorial 1: Falling Letters
function TutorialFallingLetters() {
  const [letters, setLetters] = useState([]);

  useEffect(() => {
    const letterChars = 'WORDALANCHE'.split('');
    let index = 0;

    const interval = setInterval(() => {
      if (index < 8) {
        const newLetter = {
          id: Date.now(),
          char: letterChars[index % letterChars.length],
          col: Math.floor(Math.random() * 5),
          color: ['cyan', 'pink', 'yellow', 'green', 'purple'][Math.floor(Math.random() * 5)]
        };
        setLetters(prev => [...prev.slice(-7), newLetter]);
        index++;
      }
    }, 800);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="tutorial-module">
      <div className="tutorial-screen falling-demo">
        <div className="demo-grid">
          {[0, 1, 2, 3, 4].map(col => (
            <div key={col} className="demo-column">
              {letters
                .filter(l => l.col === col)
                .map(letter => (
                  <div
                    key={letter.id}
                    className={`demo-tile falling tile-${letter.color}`}
                  >
                    {letter.char}
                  </div>
                ))}
            </div>
          ))}
        </div>
        <div className="danger-line"></div>
      </div>
      <div className="tutorial-info">
        <h3>Letters Fall From Above</h3>
        <p>Tiles drop onto a 6x8 grid, getting faster as you level up. Don't let them reach the danger line!</p>
      </div>
    </div>
  );
}

// Tutorial 2: Select Letters to Spell Words
function TutorialSelectLetters() {
  const [step, setStep] = useState(0);
  const [selectedIndices, setSelectedIndices] = useState([]);
  const gridLetters = ['P', 'X', 'L', 'A', 'M', 'Y', 'S', 'Q', 'R'];
  const selectOrder = [0, 2, 3, 5, 6]; // P, L, A, Y, S positions

  useEffect(() => {
    const interval = setInterval(() => {
      setStep(prev => {
        const next = (prev + 1) % 8;
        if (next < 5) {
          setSelectedIndices(selectOrder.slice(0, next + 1));
        } else if (next === 5) {
          // Submit animation
        } else if (next === 6) {
          // Score shown
        } else {
          setSelectedIndices([]);
        }
        return next;
      });
    }, 700);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="tutorial-module">
      <div className="tutorial-screen select-demo">
        <div className="word-preview-demo">
          {selectedIndices.map((idx, i) => (
            <span key={i} className="preview-letter">{gridLetters[idx]}</span>
          ))}
          {selectedIndices.length === 0 && <span className="preview-placeholder">Tap letters...</span>}
        </div>
        <div className="demo-grid-3x3">
          {gridLetters.map((letter, idx) => (
            <div
              key={idx}
              className={`demo-tile ${selectedIndices.includes(idx) ? 'selected' : ''} ${
                selectOrder.includes(idx) ? 'tile-cyan' : 'tile-dim'
              }`}
            >
              {letter}
              {selectedIndices.includes(idx) && (
                <span className="select-number">{selectedIndices.indexOf(idx) + 1}</span>
              )}
            </div>
          ))}
        </div>
        <div className={`submit-btn-demo ${step === 5 ? 'pressing' : ''}`}>
          SUBMIT
        </div>
        {step === 6 && <div className="score-popup">+150</div>}
      </div>
      <div className="tutorial-info">
        <h3>Tap to Spell Words</h3>
        <p>Select letters to build words (3+ letters). Longer words score way more - a 7-letter word earns 10x points!</p>
      </div>
    </div>
  );
}

// Tutorial 3: Combo System
function TutorialComboSystem() {
  const [combo, setCombo] = useState(1);
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setCombo(prev => {
        const next = prev >= 8 ? 1 : prev + 1;
        if (next > 1) setFlash(true);
        setTimeout(() => setFlash(false), 300);
        return next;
      });
    }, 1200);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="tutorial-module">
      <div className="tutorial-screen combo-demo">
        <div className="combo-timer-bar">
          <div className="combo-timer-label">COMBO WINDOW</div>
          <div className="combo-timer-track">
            <div className={`combo-timer-fill ${combo > 1 ? 'active' : ''}`}></div>
          </div>
          <div className="combo-timer-time">6s</div>
        </div>
        <div className={`combo-display ${flash ? 'flash' : ''}`}>
          <span className="combo-x">{combo}x</span>
          <span className="combo-label">COMBO</span>
        </div>
        <div className="combo-words">
          {['CAT', 'DOG', 'RUN', 'FLY', 'HOP'].slice(0, Math.min(combo, 5)).map((word, i) => (
            <div key={i} className={`combo-word ${i === Math.min(combo, 5) - 1 ? 'latest' : ''}`}>
              {word} <span className="combo-word-pts">+{(i + 1) * 40}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="tutorial-info">
        <h3>Build Combos</h3>
        <p>Submit words within 6 seconds of each other to build combos up to 8x. Speed is everything!</p>
      </div>
    </div>
  );
}

// Tutorial 4: Special Tiles
function TutorialSpecialTiles() {
  const [activeIdx, setActiveIdx] = useState(0);

  const tiles = [
    { emoji: '\u{1F4A3}', name: 'Bomb', desc: 'Use before timer hits 0 or game over!', color: 'bomb' },
    { emoji: '\u{2744}\u{FE0F}', name: 'Ice', desc: 'Tap twice to shatter. 3x bonus!', color: 'ice' },
    { emoji: '\u{1F308}', name: 'Rainbow', desc: 'Random letter. 3x word multiplier!', color: 'rainbow' },
    { emoji: '\u{2B50}', name: 'Star', desc: '2x on ALL letters for 15 seconds!', color: 'star' },
    { emoji: '2x', name: '2x Tile', desc: 'Doubles this letter\'s point value', color: 'multi2' },
    { emoji: '3x', name: '3x Tile', desc: 'Triples this letter\'s point value', color: 'multi3' },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIdx(prev => (prev + 1) % tiles.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [tiles.length]);

  const active = tiles[activeIdx];

  return (
    <div className="tutorial-module">
      <div className="tutorial-screen special-tiles-demo">
        <div className="special-tile-showcase">
          <div className={`special-tile-big tile-special-${active.color}`}>
            <span className="special-tile-emoji">{active.emoji}</span>
          </div>
          <div className="special-tile-name">{active.name}</div>
          <div className="special-tile-desc">{active.desc}</div>
        </div>
        <div className="special-tiles-strip">
          {tiles.map((tile, i) => (
            <div
              key={i}
              className={`special-tile-mini ${i === activeIdx ? 'active' : ''}`}
            >
              <span>{tile.emoji}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="tutorial-info">
        <h3>6 Special Tile Types</h3>
        <p>Bombs, ice, rainbows, stars, and multipliers - each one changes how you play!</p>
      </div>
    </div>
  );
}

// Tutorial 5: Skillshot Bonus
function TutorialSkillshot() {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setPhase(prev => (prev + 1) % 6);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const targetLength = 5;
  const word = 'SCORE';

  return (
    <div className="tutorial-module">
      <div className="tutorial-screen skillshot-demo">
        <div className="skillshot-target">
          <span className="target-label">TARGET</span>
          <span className={`target-number ${phase >= 2 && phase <= 4 ? 'matched' : ''}`}>
            {targetLength} LETTERS
          </span>
        </div>
        <div className="word-display-demo">
          {word.split('').map((letter, i) => (
            <div
              key={i}
              className={`demo-tile tile-gradient ${phase >= 1 ? 'visible' : ''}`}
              style={{ animationDelay: `${i * 0.1}s` }}
            >
              {letter}
            </div>
          ))}
        </div>
        {phase >= 3 && phase <= 5 && (
          <div className="skillshot-bonus">
            <span className="bonus-text">SKILLSHOT</span>
            <span className="bonus-amount">+50%</span>
          </div>
        )}
        <div className="score-display-demo">
          <span className={`points ${phase >= 2 ? 'counting' : ''}`}>
            {phase < 2 ? '0' : phase < 4 ? '200' : '300'}
          </span>
        </div>
      </div>
      <div className="tutorial-info">
        <h3>Hit the Skillshot</h3>
        <p>Match the target word length for +50% bonus. Targets cycle through 3, 4, 5, 6, and 7 letters!</p>
      </div>
    </div>
  );
}

// Tutorial 6: Power-Ups
function TutorialPowerUps() {
  const [activePower, setActivePower] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActivePower(prev => (prev + 1) % 4);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const powers = [
    { icon: 'ROW', name: 'Row', cost: '30', effect: 'row' },
    { icon: 'COL', name: 'Column', cost: '40', effect: 'col' },
    { icon: 'NEW', name: 'Refresh', cost: '20', effect: 'refresh' },
    { icon: 'FRZ', name: 'Freeze', cost: '50', effect: 'freeze' }
  ];

  const gridLetters = [
    ['W', 'O', 'R', 'D', 'S'],
    ['A', 'L', 'A', 'N', 'C'],
    ['H', 'E', 'G', 'A', 'M'],
    ['E', 'P', 'L', 'A', 'Y']
  ];

  return (
    <div className="tutorial-module">
      <div className="tutorial-screen powerups-demo">
        <div className={`mini-grid effect-${powers[activePower].effect}`}>
          {gridLetters.map((row, rowIdx) => (
            <div key={rowIdx} className="mini-row">
              {row.map((letter, colIdx) => (
                <div
                  key={colIdx}
                  className={`mini-tile ${
                    (powers[activePower].effect === 'row' && rowIdx === 1) ||
                    (powers[activePower].effect === 'col' && colIdx === 2) ||
                    (powers[activePower].effect === 'refresh') ||
                    (powers[activePower].effect === 'freeze')
                      ? 'affected' : ''
                  }`}
                >
                  {letter}
                </div>
              ))}
            </div>
          ))}
          {powers[activePower].effect === 'freeze' && (
            <div className="freeze-overlay">
              <span>FROZEN</span>
            </div>
          )}
        </div>
        <div className="powers-bar">
          {powers.map((power, idx) => (
            <div
              key={idx}
              className={`power-btn ${activePower === idx ? 'active' : ''}`}
            >
              <span className="power-icon">{power.icon}</span>
              <span className="power-cost">{power.cost}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="tutorial-info">
        <h3>4 Power-Ups</h3>
        <p>Spend gems to clear rows, columns, refresh all letters, or freeze bomb timers!</p>
      </div>
    </div>
  );
}

export default GameTutorial;
